import React, {useEffect, useLayoutEffect, useMemo, useState, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import './canvas.css'
import {sendMouse, sendClear, sendSettings, sendUndo, sendUndoPoint} from "../../api";
import Layers from '../layer/Layers.js';
import Picker from '../picker/Picker.js';
import Blender from '../blender/Blender.js';
import Tools from './Tools.js';
import ChatBox, {ChatInput} from '../chatbox/ChatBox.js';
import {rgba2rgb} from '../../utils/utils.js';
import {drawLine} from '../../utils/drawLine.js';
import {rgbaToHex} from '../../utils/colors';
import {commitPendingIfMatch} from '../../utils/recents';
import {CANVAS_W as w, CANVAS_H as h, TOOLBAR_W, CHAT_SIDEBAR_W, GAP_W} from './layout.js';
import useResponsiveLayout from './useResponsiveLayout.js';
import PlayersPanel from './PlayersPanel.js';
import GuessStatusBar from './GuessStatusBar.js';
import RoomHeader from './RoomHeader.js';
import KickPopup from './KickPopup.js';
import WordsetMarquee, {WordChoiceOverlay, WordRevealOverlay, VotePanel} from './overlays.js';

// Line types
const line_types = [0, 1];

// Free draw tools' line register interval (ms) — time-based throttle
const strokes_register_frequency = 1;
// Minimum distance (canvas px) a stroke must travel before registering.
// Scales with brush size: large brushes need less precision.
// At size 1 → ~1.5px, at size 50 → ~6px. Tweak the multiplier to taste.
const MIN_STROKE_DIST_BASE = 3.0;
const MIN_STROKE_DIST_SCALE = 0.1; // added per brush-size px
// Frequencies of update over network
const mouse_update_frequency = 50;
const strokes_update_frequency = 50;


export default function Drawing(props) {
    const {t} = useTranslation();
    const penIsDownRef = React.useRef(-1);
    const previousUpdateRef = React.useRef(0);
    const previousStrokeRegisterRef = React.useRef(0);
    const previousStrokeUpdateRef = React.useRef(0);
    const [strokeSettings, setStrokeSettings] = React.useState(() => ({
        t: 0, s: window.innerWidth <= 768 ? 5 : 1
    }));
    // Reset blend mode and color when our drawing round ends.
    React.useEffect(() => {
        if (!props.drawerEndVersion) return;
        setStrokeSettings(prev => ({ t: prev.t, s: prev.s }));
        setColor({});
    }, [props.drawerEndVersion]);
    const brushCursorRef = React.useRef(null);
    const brushPositionRef = React.useRef(null); // last cursor position for reapply after render
    const ctrlPipetteRef = React.useRef(null); // stores tool type to restore after Ctrl-pipette
    const sidebarRef = React.useRef(null);
    const [sidebarMaxHeight, setSidebarMaxHeight] = React.useState('100vh');
    const [color, setColor] = React.useState({});
    const [canvasLocation, setCanvasLocation] = React.useState([0, 0]);
    const wordChoiceActive = !!(props.guessRound && props.guessRound.choosing && props.guessRound.wordChoices && props.guessRound.drawer === props.user);
    const layout = useResponsiveLayout({isSpectator: props.isSpectator, isGuessMode: props.isGuessMode});
    const {
        isMobile, isMobileLandscape,
        showRightPanel, showChatSidebar, showLeftTools, showInlinePlayers,
        scale, SPECTATOR_CHAT_H,
    } = layout;
    const lastSentMouseRef = React.useRef(null);
    const totalStrokesRef = React.useRef(0);
    const [debugStrokes, setDebugStrokes] = React.useState(0);
    React.useEffect(() => {
        if (!isLocalhost) return;
        totalStrokesRef.current = 0;
        setDebugStrokes(0);
    }, [props.clearVersion]);
    const isLocalhost = window.location.hostname === 'localhost';
    const activePointerRef = React.useRef(null);
    // Temp preview line state: stores ImageData patch to erase the previous preview
    const previewPatchRef = React.useRef(null); // {imageData, x, y}
    // Stable refs for props used in event handlers (avoids stale closures)
    const sendLocalStrokesRef = React.useRef(props.sendLocalStrokes);
    sendLocalStrokesRef.current = props.sendLocalStrokes;

    const [kickTarget, setKickTarget] = useState(null); // name of player to show kick popup for

    // Bot/automation hook: lets a Puppeteer-driven client (or anything else)
    // set the active stroke settings programmatically. The same shape the
    // tools panel writes is accepted: { s, c, t }. Anything missing is left
    // unchanged. Mirrors what the UI does, including sendSettings to the
    // server so other clients see the right preview.
    React.useEffect(() => {
        window.__suttuSetTool = (next) => {
            if (!next || typeof next !== 'object') return false;
            setStrokeSettings(prev => {
                const merged = {...prev};
                if (typeof next.s === 'number' && isFinite(next.s)) merged.s = Math.max(1, Math.min(150, next.s));
                if (typeof next.t === 'number' && (next.t === 0 || next.t === 1 || next.t === 2)) merged.t = next.t;
                if (typeof next.c === 'string' && next.c.length > 0) merged.c = next.c;
                try { sendSettings(merged); } catch (_) {}
                return merged;
            });
            return true;
        };
        return () => { try { delete window.__suttuSetTool; } catch (_) {} };
    }, []);

    // Get the selected canvas' rect for coordinate mapping.
    let rect = null;
    if (props.canvases) {
        const layer = props.canvases.get(props.layer);
        if (layer) {
            rect = layer.getBoundingClientRect();
            layer.oncontextmenu = e => e.preventDefault();
        }
    }

    // displayScale: canvas CSS pixels per drawing pixel (same as `scale` but
    // derived from the actual rendered rect when available, for accuracy).
    const displayScale = rect ? rect.width / w : scale;

    // Reapply brush cursor position after React re-renders (prevents React
    // from resetting the imperatively-set top/left back to the JSX defaults).
    useLayoutEffect(() => {
        if (brushCursorRef.current && brushPositionRef.current) {
            brushCursorRef.current.style.top = brushPositionRef.current.top;
            brushCursorRef.current.style.left = brushPositionRef.current.left;
        }
    });

    // Compute sidebar maxHeight from its top offset to the viewport so the
    // bottom (add/remove layer buttons etc.) is always reachable via internal scroll.
    useLayoutEffect(() => {
        function update() {
            if (!sidebarRef.current) return;
            var top = sidebarRef.current.getBoundingClientRect().top;
            var available = Math.max(120, window.innerHeight - top);
            setSidebarMaxHeight(available + 'px');
        }
        update();
        window.addEventListener('resize', update);
        return function() { window.removeEventListener('resize', update); };
    });

    // Ctrl+Z / Cmd+Z undo  +  Ctrl-hold pipette.
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (props.drawingDisabled) return;
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
                e.preventDefault();
                sendUndo();
                props.onLocalUndo();
            }
            // Ctrl-hold → pipette (only on first press, not repeat)
            if (e.key === 'Control' && !e.repeat && ctrlPipetteRef.current === null) {
                setStrokeSettings(prev => {
                    if (prev.t === 2) return prev; // already pipette
                    ctrlPipetteRef.current = prev.t;
                    return {...prev, t: 2};
                });
            }
        };
        const handleKeyUp = (e) => {
            if (e.key === 'Control' && ctrlPipetteRef.current !== null) {
                const restore = ctrlPipetteRef.current;
                ctrlPipetteRef.current = null;
                setStrokeSettings(prev => ({...prev, t: restore}));
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [props.drawingDisabled]);

    // When drawing becomes disabled mid-stroke (e.g. the round ended while the
    // user still has the pen down), discard all transient drawing state. Without
    // this the next pointermove after re-enable would resume the stale stroke
    // and produce a long line across the canvas.
    useEffect(() => {
        if (!props.drawingDisabled) return;
        penIsDownRef.current = -1;
        props.currentStrokeRef.current = null;
        activePointerRef.current = null;
        // Wipe any in-progress preview from the prediction canvas.
        if (previewPatchRef.current && props.predictionCanvasRef && props.predictionCanvasRef.current) {
            const rp = previewPatchRef.current;
            try {
                props.predictionCanvasRef.current.getContext('2d').putImageData(rp.imageData, rp.x, rp.y);
            } catch (_) {}
            previewPatchRef.current = null;
        }
    }, [props.drawingDisabled]);

    // Flush local strokes to server (throttled). Called directly from event handlers.
    function flushStrokesIfNeeded() {
        const now = Date.now();
        if (props.localStrokesRef.current.length > 0 && now - previousStrokeUpdateRef.current > strokes_update_frequency) {
            previousStrokeUpdateRef.current = now;
            sendLocalStrokesRef.current();
        }
    }

    // Flush all remaining strokes immediately + mark undo point.
    function flushStrokesNow() {
        previousStrokeUpdateRef.current = Date.now();
        if (props.localStrokesRef.current.length > 0) {
            sendLocalStrokesRef.current();
        }
        sendUndoPoint();
    }

    // Map client coordinates to canvas drawing coordinates, accounting for scale.
    function toCanvas(clientX, clientY) {
        if (!rect) return {x: 0, y: 0};
        return {
            x: (clientX - rect.left) / displayScale,
            y: (clientY - rect.top) / displayScale,
        };
    }

    function onPointerMove(e) {
        // Ignore secondary touches (multi-finger)
        if (activePointerRef.current !== null && e.pointerId !== activePointerRef.current) return;
        if (!props.layer) return;

        const now = Date.now();
        if (!rect) return;

        const {x: event_x, y: event_y} = toCanvas(e.clientX, e.clientY);

        if (penIsDownRef.current !== -1 && !props.drawingDisabled) {
            if (line_types.includes(strokeSettings.t) && now - previousStrokeRegisterRef.current > strokes_register_frequency) {
                const cs = props.currentStrokeRef.current;
                if (cs) {
                    // Distance-based culling: skip tiny movements that won't be visible.
                    const dx = event_x - cs.x;
                    const dy = event_y - cs.y;
                    const dist = dx * dx + dy * dy; // squared to avoid sqrt
                    const minDist = MIN_STROKE_DIST_BASE + MIN_STROKE_DIST_SCALE * (strokeSettings.s || 1);
                    if (dist < minDist * minDist) {
                        // Too short — draw a temp preview on prediction canvas
                        // so the line looks smooth while accumulating distance.
                        const isPrev = cs.t === 0 && !cs.b;
                        if (isPrev && props.predictionCanvasRef?.current) {
                            const predCtx = props.predictionCanvasRef.current.getContext('2d');
                            // Restore previous preview patch
                            var pp = previewPatchRef.current;
                            if (pp) predCtx.putImageData(pp.imageData, pp.x, pp.y);
                            // Compute bounding box for save (brush radius padding)
                            var pad = Math.ceil((cs.s || 1) / 2) + 2;
                            var px = Math.min(cs.x, event_x) - pad;
                            var py = Math.min(cs.y, event_y) - pad;
                            var pw = Math.abs(event_x - cs.x) + pad * 2;
                            var ph = Math.abs(event_y - cs.y) + pad * 2;
                            px = Math.max(0, Math.floor(px));
                            py = Math.max(0, Math.floor(py));
                            pw = Math.min(w - px, Math.ceil(pw));
                            ph = Math.min(h - py, Math.ceil(ph));
                            if (pw > 0 && ph > 0) {
                                previewPatchRef.current = {imageData: predCtx.getImageData(px, py, pw, ph), x: px, y: py};
                                var predMap = new Map([[props.layer, props.predictionCanvasRef.current]]);
                                drawLine(predMap, {...cs, x2: event_x, y2: event_y, l: props.layer}, cs.t);
                            }
                        }
                    } else {
                        // Restore prediction canvas before drawing real stroke
                        if (previewPatchRef.current && props.predictionCanvasRef?.current) {
                            var rp = previewPatchRef.current;
                            props.predictionCanvasRef.current.getContext('2d').putImageData(rp.imageData, rp.x, rp.y);
                            previewPatchRef.current = null;
                        }
                        previousStrokeRegisterRef.current = now;
                        const completeStroke = {...cs, x2: event_x, y2: event_y};
                        // Predictable = pen (type 0) with default blend (source-over)
                        const isPredictable = completeStroke.t === 0 && !completeStroke.b;
                        if (isPredictable && props.predictionCanvasRef?.current) {
                            // Draw to prediction canvas (temporary until server ack)
                            const predMap = new Map([[props.layer, props.predictionCanvasRef.current]]);
                            drawLine(predMap, {...completeStroke, l: props.layer}, completeStroke.t);
                        } else {
                            // Eraser/blend strokes draw directly to real canvas
                            drawLine(props.canvases, {...completeStroke, l: props.layer}, completeStroke.t);
                        }
                        props.addLocalStroke(completeStroke, isPredictable);
                        if (isLocalhost) { totalStrokesRef.current++; setDebugStrokes(totalStrokesRef.current); }
                        flushStrokesIfNeeded();
                        let newStroke = {...strokeSettings, x: event_x, y: event_y};
                        if (penIsDownRef.current === 2) newStroke.t = 1;
                        props.currentStrokeRef.current = newStroke;
                    }
                } else {
                    let newStroke = {...strokeSettings, x: event_x, y: event_y};
                    if (penIsDownRef.current === 2) newStroke.t = 1;
                    props.currentStrokeRef.current = newStroke;
                }
            }
        }

        if (now - previousUpdateRef.current > mouse_update_frequency) {
            const last = lastSentMouseRef.current;
            if (!last || last.x !== event_x || last.y !== event_y) {
                sendMouse({x: event_x, y: event_y});
                lastSentMouseRef.current = {x: event_x, y: event_y};
            }
            previousUpdateRef.current = now;
        }

        // Update brush cursor position directly (no re-render needed)
        const bTop = (e.clientY - brushScreenSize / 2 - 1) + 'px';
        const bLeft = (e.clientX - brushScreenSize / 2 - 1) + 'px';
        brushPositionRef.current = {top: bTop, left: bLeft};
        if (brushCursorRef.current) {
            brushCursorRef.current.style.top = bTop;
            brushCursorRef.current.style.left = bLeft;
        }
    }

    function handlePointerDown(e) {
        // Only track one pointer at a time (first finger / pen wins)
        if (activePointerRef.current !== null) return;
        activePointerRef.current = e.pointerId;
        try { e.target.setPointerCapture(e.pointerId); } catch (_) {}
        if (!props.layer) return;
        // If a custom color was just picked and now being drawn with, commit to recents.
        try { commitPendingIfMatch(rgbaToHex(strokeSettings.c)); } catch (_) {}
        setPen(e, e.button);
    }

    function setPen(e, down) {
        if (props.drawingDisabled) return;
        penIsDownRef.current = down;

        if (line_types.includes(strokeSettings.t)) {
            if (down === -1) {
                // Clear temp preview before final stroke
                if (previewPatchRef.current && props.predictionCanvasRef?.current) {
                    var rp = previewPatchRef.current;
                    props.predictionCanvasRef.current.getContext('2d').putImageData(rp.imageData, rp.x, rp.y);
                    previewPatchRef.current = null;
                }
                const cs = props.currentStrokeRef.current;
                if (cs) {
                    const {x, y} = toCanvas(e.clientX, e.clientY);
                    const completeStroke = {...cs, x2: x, y2: y};
                    const isPredictable = completeStroke.t === 0 && !completeStroke.b;
                    if (isPredictable && props.predictionCanvasRef?.current) {
                        const predMap = new Map([[props.layer, props.predictionCanvasRef.current]]);
                        drawLine(predMap, {...completeStroke, l: props.layer}, completeStroke.t);
                    } else {
                        drawLine(props.canvases, {...completeStroke, l: props.layer}, completeStroke.t);
                    }
                    props.addLocalStroke(completeStroke, isPredictable);
                    props.currentStrokeRef.current = null;
                    flushStrokesNow();
                }
            }
        }
    }

    function handlePointerUp(e) {
        if (e.pointerId !== activePointerRef.current) return;
        activePointerRef.current = null;
        try { e.target.releasePointerCapture(e.pointerId); } catch (_) {}
        if (!props.layer) return;
        if (props.drawingDisabled) return;

        if (strokeSettings.t === 2) {
            if (!rect) return;
            const {x: event_x, y: event_y} = toCanvas(e.clientX, e.clientY);
            const canvas = props.canvases.get(props.layer);
            const ctx = canvas.getContext('2d');
            const colorData = ctx.getImageData(event_x, event_y, 1, 1).data;
            const colorString = rgba2rgb(colorData);
            // Restore previous tool if this was a Ctrl-hold pipette pick
            if (ctrlPipetteRef.current !== null) {
                const restore = ctrlPipetteRef.current;
                ctrlPipetteRef.current = null;
                setStrokeSettings(prev => ({...prev, c: colorString, t: restore}));
            } else {
                setStrokeSettings({...strokeSettings, c: colorString});
            }
        }

        if (line_types.includes(strokeSettings.t)) {
            if (!props.currentStrokeRef.current && penIsDownRef.current !== -1)
                createDotStroke(e);
        }
        setPen(e, -1);
    }

    function handlePointerLeave(e) {
        if (e.pointerId !== activePointerRef.current) return;
        activePointerRef.current = null;
        setPen(e, -1);
    }

    function wheel(e) {
        if (props.drawingDisabled) return;
        if (!line_types.includes(strokeSettings.t)) return;

        let changeAmount = 1;
        if (strokeSettings.s > 60) changeAmount = 12;
        else if (strokeSettings.s > 20) changeAmount = 6;
        else if (strokeSettings.s > 5) changeAmount = 2;
        if (e.altKey) changeAmount *= 4;

        if (e.nativeEvent.deltaY < 0)
            setStrokeSettings({...strokeSettings, s: Math.min(150, strokeSettings.s + changeAmount)});
        else
            setStrokeSettings({...strokeSettings, s: Math.max(1, strokeSettings.s - changeAmount)});

        updateToolSettings();
    }

    function createDotStroke(e) {
        if (!rect) return;
        const {x, y} = toCanvas(e.clientX, e.clientY);
        let completeStroke = {
            ...strokeSettings,
            x, y, x2: x, y2: y,
        };
        if (penIsDownRef.current === 2) completeStroke.t = 1;
        props.addLocalStroke(completeStroke);
        props.currentStrokeRef.current = null;
        flushStrokesNow();
    }

    const updateToolSettings = () => {
        sendSettings(strokeSettings);
    };

    const handleClear = () => {
        sendClear();
    };

    const handleColorChange = (color) => {
        const col = color.rgb;
        setColor(color);
        setStrokeSettings(s => ({...s, c: `rgba(${col.r},${col.g},${col.b},${col.a})`}));
    };

    const renderCanvases = () => {
        const length = props.layerList.length;
        const activeIndex = props.layerList.indexOf(props.layer);
        const canvasCursor = !isMobile && strokeSettings.t === 2 ? 'crosshair' : undefined;
        const layers = props.layerList.map((layer, index) => {
            return <canvas
                key={layer}
                ref={r => props.canvasRef(layer, r)}
                className="Canvas"
                style={{
                    width: w * scale, height: h * scale,
                    pointerEvents: index === 0 ? "auto" : "none",
                    top: canvasLocation[1], left: canvasLocation[0],
                    touchAction: 'none',
                    cursor: index === 0 ? canvasCursor : undefined,
                    // Multiply by 2 to leave room for the prediction canvas between layers.
                    zIndex: (length - index) * 2,
                }}
                width={w} height={h}
                onPointerMove={index === 0 ? onPointerMove : undefined}
                onPointerDown={index === 0 ? handlePointerDown : undefined}
                onPointerUp={index === 0 ? handlePointerUp : undefined}
                onPointerLeave={index === 0 ? handlePointerLeave : undefined}
                onContextMenu={index === 0 ? (e => e.preventDefault()) : undefined}
                onWheel={index === 0 ? wheel : undefined}
            />;
        });
        // Prediction canvas — sits just above the active layer, below any layers on top of it.
        const predZIndex = activeIndex >= 0
            ? (length - activeIndex) * 2 + 1
            : length * 2 + 1;
        layers.push(
            <canvas
                key="__prediction__"
                ref={props.predictionCanvasRef}
                className="Canvas"
                style={{
                    width: w * scale, height: h * scale,
                    pointerEvents: "none",
                    top: canvasLocation[1], left: canvasLocation[0],
                    touchAction: 'none',
                    zIndex: predZIndex,
                }}
                width={w} height={h}
            />
        );
        return layers;
    };

    // Visual brush size in screen pixels (accounts for canvas scale).
    const brushScreenSize = strokeSettings.s * displayScale;

    // Memoize Layers to avoid unnecessary re-renders (must be called unconditionally).
    const layersPanel = useMemo(() =>
        <Layers layerList={props.layerList}
                moveLayer={props.moveLayer}
                setLayer={props.setLayer}
                canvases={props.canvases}
                layer={props.layer}
                thumbs={props.thumbs}
                thumbRef={props.thumbRef}
                disabled={props.drawingDisabled}
        />
    , [props.layer, props.layerList, props.drawingDisabled]);

    return (
        <>
        {isLocalhost && (
            <div style={{
                position: 'fixed', bottom: 4, left: 4, zIndex: 9999,
                background: 'rgba(0,0,0,0.65)', color: '#0f0',
                fontFamily: 'monospace', fontSize: 11, padding: '2px 6px',
                borderRadius: 3, pointerEvents: 'none',
            }}>
                strokes: {debugStrokes}
            </div>
        )}
        <div className="Col" style={{position: 'relative', zIndex: 1, ...(isMobileLandscape ? {maxHeight: '100vh', overflow: 'hidden'} : {})}}>
            {props.room && !props.isGuessMode && (
                <RoomHeader
                    room={props.room}
                    homeButton={props.homeButton}
                    isOwner={props.isOwner}
                    isPrivate={props.isPrivate}
                    isMobile={isMobile}
                    isMobileLandscape={isMobileLandscape}
                    isSpectator={props.isSpectator}
                    onPublish={props.onPublish}
                    publishPending={props.publishPending}
                />
            )}

            {props.isGuessMode && (
                <GuessStatusBar
                    currentUser={props.user}
                    homeButton={props.homeButton}
                    room={props.room}
                    guessRound={props.guessRound}
                    guessIntermission={props.guessIntermission}
                    everyoneDraws={props.everyoneDraws}
                    myVote={props.myVote}
                    isMobile={isMobile}
                    isMobileLandscape={isMobileLandscape}
                    isSpectator={props.isSpectator}
                    onSkip={props.onSkip}
                    onPublish={props.onPublish}
                    publishPending={props.publishPending}
                />
            )}

            <div className="Row" style={{alignItems: 'flex-start', position: 'relative'}}>
                {showLeftTools && (
                <Tools strokeSettings={strokeSettings}
                       handleClear={handleClear}
                       setStrokeSettings={setStrokeSettings}
                       onUndo={() => { sendUndo(); props.onLocalUndo(); }}
                       drawingDisabled={props.drawingDisabled}
                       isGuessMode={props.isGuessMode}
                       onPublish={props.onPublish}
                       publishPending={props.publishPending}
                       compact={isMobileLandscape || undefined}/>
                )}

                {/* Canvas column: white background + docked chat pinned below it */}
                <div className="Col" style={{width: (isMobile && !isMobileLandscape) ? '100%' : undefined}}>
                    <div className="CanvasContainer" style={{
                        width: w * scale, height: h * scale, position: 'relative', touchAction: 'none',
                        zIndex: wordChoiceActive ? 50 : 0,
                    }}
                         ref={el => {
                             if (!el) return;
                             var l = el.offsetLeft, t = el.offsetTop;
                             if (canvasLocation[0] !== l || canvasLocation[1] !== t) {
                                 setCanvasLocation([l, t]);
                             }
                         }}>
                        {/* Drawing-disabled overlay */}
                        {props.drawingDisabled && (
                            <div style={{
                                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                zIndex: 100, cursor: 'default',
                            }}/>
                        )}
                        <WordChoiceOverlay
                            guessRound={props.guessRound}
                            currentUser={props.user}
                            onWordChoice={props.onWordChoice}
                        />
                    </div>

                    <VotePanel
                        guessIntermission={props.guessIntermission}
                        currentUser={props.user}
                        isMobileLandscape={isMobileLandscape}
                        myVote={props.myVote}
                        onVote={props.onVote}
                        width={w * scale}
                    />

                    {/* Mobile horizontal toolbar — between canvas and chat (portrait only) */}
                    {isMobile && !isMobileLandscape && !props.drawingDisabled && (
                        <Tools strokeSettings={strokeSettings}
                               handleClear={handleClear}
                               setStrokeSettings={setStrokeSettings}
                               onUndo={() => { sendUndo(); props.onLocalUndo(); }}
                               drawingDisabled={props.drawingDisabled}
                               isGuessMode={props.isGuessMode}
                               onPublish={props.onPublish}
                               publishPending={props.publishPending}
                               horizontal/>
                    )}

                    {/* Chat + Players row (portrait mobile + non-landscape spectator only) */}
                    {props.chatMessages != null && !showChatSidebar && (
                        <div style={{
                            display: 'flex', flexDirection: 'row',
                            width: (isMobile && !isMobileLandscape) ? '100%' : w * scale,
                            marginTop: (isMobile && !isMobileLandscape) ? 0 : 4,
                            gap: showInlinePlayers ? 2 : 0,
                            alignItems: 'stretch',
                            ...(isMobile && !isMobileLandscape ? {maxHeight: 160} : {}),
                            ...(props.isSpectator && !isMobile && !isMobileLandscape ? {height: SPECTATOR_CHAT_H, flexShrink: 0} : {}),
                        }}>
                            <div style={{flex: showInlinePlayers ? 3 : undefined, width: showInlinePlayers ? undefined : '100%', minWidth: 0, display: 'flex', flexDirection: 'column'}}>
                                <ChatBox user={props.user} chatMessages={props.chatMessages} docked
                                         isDrawer={props.isDrawer}
                                         roundActive={props.roundActive}
                                         isGuessMode={props.isGuessMode}
                                         rateLimited={props.rateLimited}
                                         setRateLimited={props.setRateLimited}
                                         compact={isMobile}
                                         correctGuessers={props.correctGuessers}
                                         isSpectator={props.isSpectator}/>
                            </div>
                            {/* Inline players panel (mobile + spectator) */}
                            {showInlinePlayers && (
                                <PlayersPanel
                                    variant="compact"
                                    wrapperStyle={{flex: 2, minWidth: 0}}
                                    currentUser={props.user}
                                    roomOwner={props.roomOwner}
                                    isGuessMode={props.isGuessMode}
                                    guessUserList={props.guessUserList}
                                    users={props.users}
                                    guessRound={props.guessRound}
                                    isSpectator={props.isSpectator}
                                    everyoneDraws={props.everyoneDraws}
                                    selfOptedIn={props.selfOptedIn}
                                    toggleOptIn={props.toggleOptIn}
                                    selfAfk={props.selfAfk}
                                    toggleAfk={props.toggleAfk}
                                    aiAvailable={props.aiAvailable}
                                    aiAllowed={props.aiAllowed}
                                    toggleAiAllowed={props.toggleAiAllowed}
                                    onKickTarget={setKickTarget}
                                />
                            )}
                        </div>
                    )}

                    {/* Mobile full-width chat input — below both messages and players (portrait only) */}
                    {isMobile && !isMobileLandscape && props.chatMessages != null && !props.isSpectator && !(props.isGuessMode && props.isDrawer && props.roundActive) && (
                        <ChatInput
                            user={props.user}
                            isDrawer={props.isDrawer}
                            roundActive={props.roundActive}
                            isGuessMode={props.isGuessMode}
                            rateLimited={props.rateLimited}
                            setRateLimited={props.setRateLimited}
                            isSpectator={props.isSpectator}
                            correctGuessers={props.correctGuessers}
                        />
                    )}
                </div>

                {/* Landscape mobile/TV chat sidebar — right of canvas */}
                {showChatSidebar && props.chatMessages != null && (
                    <div style={{
                        marginLeft: GAP_W, width: CHAT_SIDEBAR_W,
                        display: 'flex', flexDirection: 'column',
                        height: h * scale, minHeight: 0,
                    }}>
                        {/* Players panel */}
                        <PlayersPanel
                            variant="compact"
                            wrapperStyle={{flexShrink: 0, maxHeight: '40%'}}
                            currentUser={props.user}
                            roomOwner={props.roomOwner}
                            isGuessMode={props.isGuessMode}
                            guessUserList={props.guessUserList}
                            users={props.users}
                            guessRound={props.guessRound}
                            isSpectator={props.isSpectator}
                            everyoneDraws={props.everyoneDraws}
                            selfOptedIn={props.selfOptedIn}
                            toggleOptIn={props.toggleOptIn}
                            selfAfk={props.selfAfk}
                            toggleAfk={props.toggleAfk}
                            aiAvailable={props.aiAvailable}
                            aiAllowed={props.aiAllowed}
                            toggleAiAllowed={props.toggleAiAllowed}
                            onKickTarget={setKickTarget}
                        />
                        {/* Chat box — fills remaining space */}
                        <div style={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', marginTop: 2}}>
                            <ChatBox user={props.user} chatMessages={props.chatMessages} docked
                                     isDrawer={props.isDrawer}
                                     roundActive={props.roundActive}
                                     isGuessMode={props.isGuessMode}
                                     rateLimited={props.rateLimited}
                                     setRateLimited={props.setRateLimited}
                                     compact
                                     correctGuessers={props.correctGuessers}
                                     isSpectator={props.isSpectator}/>
                        </div>
                        {/* Chat input */}
                        {!props.isSpectator && !(props.isGuessMode && props.isDrawer && props.roundActive) && (
                            <ChatInput
                                user={props.user}
                                isDrawer={props.isDrawer}
                                roundActive={props.roundActive}
                                isGuessMode={props.isGuessMode}
                                rateLimited={props.rateLimited}
                                setRateLimited={props.setRateLimited}
                                isSpectator={props.isSpectator}
                                correctGuessers={props.correctGuessers}
                            />
                        )}
                    </div>
                )}

                {/* Canvases are direct Row children so their abs-positioning (margin-left: 36px)
                    stays relative to the Row's main-start, aligning with CanvasContainer. */}
                {renderCanvases()}
                {/* Overlays rendered as canvas siblings so z-index stacking works */}
                {props.isGuessMode && (
                    <div style={{position: 'absolute', top: canvasLocation[1], left: canvasLocation[0], width: w * scale, height: h * scale, pointerEvents: 'none', zIndex: 90}}>
                        <WordsetMarquee
                            roomSettings={props.roomSettings}
                            guessRound={props.guessRound}
                            guessIntermission={props.guessIntermission}
                        />
                    </div>
                )}
                <div style={{position: 'absolute', top: canvasLocation[1], left: canvasLocation[0], width: w * scale, height: h * scale, pointerEvents: 'none', zIndex: 91}}>
                    <WordRevealOverlay
                        guessIntermission={props.guessIntermission}
                        roomSettings={props.roomSettings}
                        scale={scale}
                    />
                </div>



                {/* Sidebar: Picker, Blender, Layers, and Users in guess mode */}
                {showRightPanel && (
                <div ref={sidebarRef} className="sidebar-scroll" style={{marginLeft: GAP_W + "px", width: 235, flexShrink: 0, maxHeight: sidebarMaxHeight, overflowY: 'auto', overflowX: 'hidden', alignSelf: 'flex-start'}}>
                    {/* Players panel — visible in all game modes */}
                    <PlayersPanel
                        variant="desktop"
                        currentUser={props.user}
                        roomOwner={props.roomOwner}
                        isGuessMode={props.isGuessMode}
                        guessUserList={props.guessUserList}
                        users={props.users}
                        guessRound={props.guessRound}
                        isSpectator={props.isSpectator}
                        everyoneDraws={props.everyoneDraws}
                        selfOptedIn={props.selfOptedIn}
                        toggleOptIn={props.toggleOptIn}
                        selfAfk={props.selfAfk}
                        toggleAfk={props.toggleAfk}
                        aiAvailable={props.aiAvailable}
                        aiAllowed={props.aiAllowed}
                        toggleAiAllowed={props.toggleAiAllowed}
                        showOptInInHeader={false}
                        onKickTarget={setKickTarget}
                    />
                    {props.isGuessMode && !props.isSpectator && !props.everyoneDraws && !(props.guessRound && props.guessRound.active && props.guessRound.drawer === props.user) && (
                        <button
                            data-guess-draw-opt-in="true"
                            data-guess-opt-in-button="true"
                            data-guess-opted-in={props.selfOptedIn ? 'true' : 'false'}
                            data-guess-opt-in-state={props.selfOptedIn ? 'opted-in' : 'not-opted-in'}
                            onClick={props.toggleOptIn}
                            style={{
                                display: 'block',
                                width: 229, padding: '8px 0', marginBottom: 3,
                                fontSize: 15, fontWeight: 'bold', cursor: 'pointer',
                                background: props.selfOptedIn ? '#4a8a4a' : '#7755cc',
                                color: '#fff', border: '2px solid rgba(255,255,255,0.3)',
                                borderRadius: 4, letterSpacing: 1,
                                boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                boxSizing: 'border-box',
                                appearance: 'none', WebkitAppearance: 'none',
                            }}
                        >
                            {props.selfOptedIn ? t('drawing.dontDraw') : t('drawing.draw')}
                        </button>
                    )}
                    {!props.isSpectator && <>
                    <Picker onChangeComplete={handleColorChange} color={strokeSettings.c} isGuessMode={props.isGuessMode}/>
                    <Blender setStrokeSettings={setStrokeSettings} strokeSettings={strokeSettings}/>
                    {layersPanel}
                    </>}
                </div>
                )}
            </div>

            <div style={{position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1000}}>
                {rect && !props.isDrawer && props.users.filter(u => {
                    if (u.name === props.user) return false;
                    if (!u.x && !u.y) return false; // hide cursors that haven't moved yet (includes bots)
                    // In guess mode during active round or intermission, only show the drawer's cursor
                    if (props.isGuessMode && (props.guessRound?.active || props.guessIntermission?.active)) {
                        return u.name === props.guessRound?.drawer;
                    }
                    return true;
                }).map((usr) => (
                    <div key={usr.name} className="RemotePointer" style={{
                        top: (usr.y ? usr.y * displayScale + rect.top - (usr.s ? usr.s * displayScale / 2 : 0) : 0),
                        left: (usr.x ? usr.x * displayScale + rect.left - (usr.s ? usr.s * displayScale / 2 : 0) : 0),
                        width: usr.s ? usr.s * displayScale : "4px",
                        height: usr.s ? usr.s * displayScale : "4px",
                        borderRadius: usr.s ? usr.s * displayScale / 2 + "px" : "2px",
                    }}>
                        {!(props.isGuessMode && (props.guessRound?.active || props.guessRound?.choosing || props.guessIntermission?.active)) && usr.name}
                    </div>
                ))}
                {rect && !isMobile && strokeSettings.t !== 2 &&
                    <div ref={brushCursorRef} style={{
                        position: "absolute",
                        top: -100, left: -100,
                        width: brushScreenSize, height: brushScreenSize,
                        border: "1px solid #333",
                        borderRadius: brushScreenSize / 2 + "px",
                        borderColor: "#000", color: "#000",
                        pointerEvents: "none", zIndex: 10,
                    }}/>
                }
            </div>

            {/* Player action popup */}
            <KickPopup
                target={kickTarget}
                isOwner={props.isOwner}
                onClose={() => setKickTarget(null)}
                onMakeOwner={props.onTransferOwnership}
                onVoteKick={props.onVoteKick}
            />
        </div>
        </>
    );
}
